import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Link
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer
} from 'recharts';
import { reportService } from '../../../services/api';

const SkillsProgressionChart = () => {
    const [skillsData, setSkillsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const fetchSkillsData = async () => {
        try {
            setLoading(true);
            const response = await reportService.getSkillGraphSummary({
                page: 0,
                limit: 6,
                search: ''
            });

            if (response.data.success) {
                const transformedData = response.data.data.map(skill => ({
                    name: skill.skillName,
                    completion: skill.avgCompletionPercent
                }));
                setSkillsData(transformedData);
            }
        } catch (error) {
            console.error('Error fetching skills data:', error);
            setError('Failed to load skills data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSkillsData();
    }, []);

    const handleClick = () => {
        navigate('/reports/skill-graph-summary');
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        p: 1,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1
                    }}
                >
                    <Typography variant="body2">{label}</Typography>
                    <Typography variant="body2" color="primary">
                        {`Completion: ${payload[0].value}%`}
                    </Typography>
                </Box>
            );
        }
        return null;
    };

    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        Skills Progression
                    </Typography>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={handleClick}
                        sx={{ textDecoration: 'none' }}
                    >
                        View Details
                    </Link>
                </Box>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                            data={skillsData}
                            margin={{
                                left: 10,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="name"
                                interval={0}
                                tickFormatter={(v) => v.length > 5 ? v.slice(0, 5) + "…" : v}
                            />
                            <YAxis
                                label={{ 
                                    value: 'Completion %', 
                                    angle: -90, 
                                    position: 'insideLeft',
                                    offset: 0
                                }}
                                
                            />
                            <RechartsTooltip content={<CustomTooltip />} />
                            <Bar
                                dataKey="completion"
                                fill="var(--primary-color)"
                                name="Completion %"
                                radius={[4, 4, 0, 0]}
                                barSize={40}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
};

export default SkillsProgressionChart;